<template>
  <div v-if="hasAnyPermission('item-name-view')" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card">
      <div class="card-header bg-white py-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0 fw-bold">Standard Item Name</h5>
          <DialogPrime v-model:visible="searchDialog.visible" modal header="Search Item Name"
            style="width: 25rem; margin: 10px;">
            <div class="mb-2">
              <label for="search_keyword" class="font-semibold">INC / Item Name / Colloquial</label>
              <InputText id="search_keyword" v-model="searchForm.keyword"
                placeholder="Search by INC, Item Name, or Colloquial" class="w-100" />
            </div>
            <div class="mb-2">
              <label for="search_definition" class="font-semibold">Definition</label>
              <InputText id="search_definition" v-model="searchForm.definition"
                placeholder="Search by Definition (English or Indonesia)" class="w-100" />
            </div>
            <div class="d-flex justify-content-end gap-2 mt-3">
              <ButtonPrime type="button" label="Cancel" severity="secondary" @click="closeSearchDialog"></ButtonPrime>
              <ButtonPrime type="button" label="Search" @click="doAdvancedSearch" />
            </div>
          </DialogPrime>
        </div>
        <div class="col-4"></div>
        <div class="col-4 d-flex justify-content-end align-items-start pe-0">
          <button type="button" class="mx-1 mb-0 btn btn-sm btn-outline-primary" @click="openSearchDialog"> Filter
          </button>
          <button v-if="hasAnyPermission('item-name-create')" type="button" class="mx-1 mb-0 btn btn-sm btn-primary"
            @click="openAddModal">Add New</button>
          <!-- dialog add and update -->
          <DialogPrime v-model:visible="modal.visible" modal style="width: 30rem; margin: 10px;">
            <template #header>
              <div class="d-flex align-items-center gap-3">
                <div>
                  <h5 class="mb-0 fw-bold text-dark">{{ modal.action }}</h5>
                </div>
              </div>
            </template>
            <div class="mb-2">
              <label for="inc" class="font-semibold">INC <span class="text-danger">*</span></label>
              <InputText id="inc" v-model="modal.data.inc" placeholder="INC" class="w-100"
                :class="modal.errorMessage['inc'] ? 'border-danger' : ''"
                @input="toUpperCaseVal($event, 'inc'); clearError('inc')" />
              <small v-if="modal.errorMessage['inc']" class="text-danger">{{ modal.errorMessage['inc'] }}</small>
            </div>
            <div class="mb-2">
              <label for="item_name" class="font-semibold">Item Name <span class="text-danger">*</span></label>
              <InputText id="item_name" v-model="modal.data.item_name" placeholder="Item Name" class="w-100"
                :class="modal.errorMessage['item_name'] ? 'border-danger' : ''"
                @input="toUpperCaseVal($event, 'item_name'); clearError('item_name')" />
              <small v-if="modal.errorMessage['item_name']" class="text-danger">{{ modal.errorMessage['item_name']
              }}</small>
            </div>
            <div class="mb-2">
              <label for="short_name" class="font-semibold">Short Name <span class="text-danger">*</span></label>
              <InputText id="short_name" v-model="modal.data.short_name" placeholder="Short Name" class="w-100"
                :class="modal.errorMessage['short_name'] ? 'border-danger' : ''"
                @input="toUpperCaseVal($event, 'short_name'); clearError('short_name')" />
              <small v-if="modal.errorMessage['short_name']" class="text-danger">{{ modal.errorMessage['short_name']
              }}</small>
            </div>
            <div v-if="modal.editId" class="mb-2" style="display: none;">
              <label for="status" class="font-semibold">Status <span class="text-danger">*</span></label>
              <SelectPrime id="status" v-model="modal.data.status" :options="statusOptions" option-label="label"
                option-value="value" placeholder="Choose Status" class="w-100"
                :invalid="modal.errorMessage['status'] && !modal.data.status" :disabled="!modal.editId" fluid
                @change="clearError('status')" />
            </div>
            <div class="mb-2">
              <label for="definition_eng" class="font-semibold">Definition (English)</label>
              <PrimeTextarea id="definition_eng" v-model="modal.data.definition_eng" placeholder="Definition English"
                rows="4" style="resize: none;" class="w-100" />
            </div>
            <div class="mb-2">
              <label for="definition_ind" class="font-semibold">Definition (Indonesia)</label>
              <PrimeTextarea id="definition_ind" v-model="modal.data.definition_ind" placeholder="Definition Indonesia"
                rows="4" style="resize: none;" class="w-100" />
            </div>
            <template #footer>
              <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
                <ButtonPrime type="button" label="Cancel" severity="danger" raised size="small" class="px-4 border-0"
                  @click="closeModal()"></ButtonPrime>
                <ButtonPrime v-if="canSaveItemName" type="button" severity="contrast" raised size="small" label="Save"
                  class="px-4" :disabled="modal.onLoading" @click="save()">
                  <template v-if="modal.onLoading" #icon>
                    <i class="fas fa-spinner fa-spin me-2"></i>
                  </template>
                  <span v-if="modal.onLoading">Please wait...</span>
                  <span v-else>{{ modal.editId ? 'Save' : 'Add' }}</span>
                </ButtonPrime>
              </div>
            </template>
          </DialogPrime>
        </div>
      </div>
      <div style="height: 300px; overflow-y: auto;">
        <DataTable :value="table.onLoad ? dummyRows : table.data" class="datatable-sm text-sm" size="null"
          removable-sort scrollable scroll-height="flex" :row-class="rowClass" @row-click="selectRow">
          <Column field="inc" header="Inc" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.inc ? slotProps.data.inc : '' }}
            </template>
          </Column>
          <Column field="item_name" header="Item Name" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.item_name ? slotProps.data.item_name : '' }}
            </template>
          </Column>
          <Column field="short_name" header="Short Name" header-class="bg-light text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.short_name ? slotProps.data.short_name : '' }}
            </template>
          </Column>
          <Column header="Actions" header-class="bg-light" :style="{ width: '150px' }"
            :body-style="{ width: '150px', padding: '0' }">
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              <ButtonPrime v-if="hasAnyPermission('item-name-update')" v-tooltip.bottom="'Update'" icon="pi pi-pencil"
                outlined rounded size="small" class="me-2" @click="openEditModal(slotProps.data)" />
              <ButtonPrime v-if="hasAnyPermission('item-name-delete')" v-tooltip.bottom="'Delete'" icon="pi pi-trash"
                outlined rounded severity="danger" size="small" @click="deleteItem(slotProps.data.id)" />
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">
                No Data
              </div>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination v-model="table.page.current_pages" :page-count="table.page?.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_pages - 1) * table.page.per_pages + table.data.length }} of {{ table.total }}
        </p>
      </div>
    </div>

    <!-- Card Kedua dengan Tabs PrimeVue -->
    <div class="card mt-4">
      <div class="card-body">
        <TabView>
          <TabPanel header="Definition">
            <div v-if="detailLoading">
              <Skeleton height="2rem" class="mb-2" />
              <Skeleton width="60%" height="1.5rem" class="mb-2" />
              <Skeleton width="80%" height="1.5rem" class="mb-2" />
              <Skeleton width="40%" height="1.5rem" class="mb-2" />
            </div>
            <div v-else-if="selectedDetail">
              <div class="mt-3 mb-2">
                <ButtonPrime size="small" :severity="activeDefinitionLang === 'eng' ? 'primary' : 'secondary'"
                  class="me-2" @click="activeDefinitionLang = 'eng'">English</ButtonPrime>
                <ButtonPrime size="small" :severity="activeDefinitionLang === 'ind' ? 'primary' : 'secondary'"
                  @click="activeDefinitionLang = 'ind'">Indonesia</ButtonPrime>
              </div>
              <div>
                <div class="mt-2">
                  <span v-if="activeDefinitionLang === 'eng'">{{ selectedDetail.definition_eng || '-' }}</span>
                  <span v-else>{{ selectedDetail.definition_ind || '-' }}</span>
                </div>
              </div>
            </div>
            <div v-else class="text-muted">Select an item from the table above.</div>
          </TabPanel>
          <!-- Colloquial -->
          <TabPanel header="Colloquial">
            <div v-if="detailLoading">
              <Skeleton height="2rem" class="mb-2" />
              <Skeleton width="80%" height="1.5rem" class="mb-2" />
              <Skeleton width="60%" height="1.5rem" class="mb-2" />
            </div>
            <div v-else-if="selectedDetail">
              <div class="mt-2 mb-2 d-flex justify-content-end">
                <ButtonPrime v-if="hasAnyPermission('item-name-create-colloquial')" icon="pi pi-plus" label="Add"
                  size="small" style="background-color: black; color: white; border: none;"
                  @click="addColloquialDialog = true" />
              </div>
              <DataTable v-if="selectedDetail.item_name_colloquial && selectedDetail.item_name_colloquial.length"
                :value="selectedDetail.item_name_colloquial" class="datatable-sm text-sm" scrollable
                scroll-height="200px">
                <Column field="colloquial_name" header="Colloquial" header-class="bg-light text-dark" />
                <Column header="Actions" header-class="bg-light" :style="{ width: '150px' }">
                  <template #body="slotProps">
                    <ButtonPrime v-if="hasAnyPermission('item-name-delete-colloquial')" v-tooltip.bottom="'Delete'"
                      icon="pi pi-trash" severity="danger" rounded size="small"
                      @click="deleteColloquial(slotProps.data.id)" />
                  </template>
                </Column>
              </DataTable>
              <div v-else class="text-muted">Not Found data colloquial.</div>

              <DialogPrime v-model:visible="addColloquialDialog" modal header="Add Colloquial" style="width: 25rem;">
                <div class="mb-2">
                  <label>Colloquial Name<span class="text-danger">*</span></label>
                  <InputText v-model="addColloquialForm.colloquial_name" placeholder="Enter Colloquial Name"
                    :class="addColloquialError ? 'border-danger' : ''" fluid />
                </div>
                <template #footer>
                  <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
                    <ButtonPrime type="button" label="Cancel" severity="danger" raised size="small"
                      class="px-4 border-0" @click="addColloquialDialog = false" />
                    <ButtonPrime type="button" severity="contrast" raised size="small" label="Add" class="px-4"
                      :disabled="addColloquialLoading" @click="addColloquial">
                      <template v-if="addColloquialLoading" #icon>
                        <i class="fas fa-spinner fa-spin me-2"></i>
                      </template>
                    </ButtonPrime>
                  </div>
                </template>
              </DialogPrime>
            </div>
          </TabPanel>
          <!-- NGC -->
          <TabPanel header="NGC">
            <div v-if="detailLoading">
              <Skeleton height="2rem" class="mb-2" />
              <Skeleton width="80%" height="1.5rem" class="mb-2" />
              <Skeleton width="60%" height="1.5rem" class="mb-2" />
            </div>

            <div v-else-if="selectedDetail">
              <div class="mt-2 mb-2 d-flex justify-content-end">
                <ButtonPrime v-if="hasAnyPermission('item-name-create-group-class')" icon="pi pi-plus" label="Add"
                  size="small" style="background-color: black; color: white; border: none;"
                  @click="addNGCDialog = true" />
              </div>

              <DataTable v-if="selectedDetail.item_name_unspsc && selectedDetail.item_name_unspsc.length"
                :value="selectedDetail.item_name_unspsc" class="datatable-sm text-sm" scrollable scroll-height="300px">

                <Column field="identity_relation_code" header="Relation Code" header-class="bg-light text-dark"
                  :style="{ width: '200px' }" />
                <Column field="unspsc_detail.definition" header="Group Definition" header-class="bg-light text-dark">
                  <template #body="slotProps">
                    {{ slotProps.data.unspsc_detail?.definition || '-' }}
                  </template>
                </Column>
                <Column header="Actions" header-class="bg-light" :style="{ width: '100px' }">
                  <template #body="slotProps">
                    <ButtonPrime v-if="hasAnyPermission('item-name-delete-group-class')" v-tooltip.bottom="'Delete'"
                      icon="pi pi-trash" severity="danger" rounded size="small"
                      @click.stop="deleteNGC(slotProps.data.id)" />
                  </template>
                </Column>
              </DataTable>
              <div v-else class="text-muted">Not Found NGC/UNSPSC.</div>

              <!--  Dialog Tambah -->
              <DialogPrime v-model:visible="addNGCDialog" modal header="Add NGC" style="width: 25rem;">
                <div class="mb-2">
                  <label>Relation Code<span class="text-danger">*</span></label>
                  <AutoComplete v-model="addNGCForm.unspscObject" :suggestions="unspscOptions" :option-label="'label'"
                    placeholder="Search Relation Code..." :dropdown="true" :force-selection="true"
                    :invalid="addNGCError !== ''" fluid @complete="searchUNSPSC" />
                </div>

                <template #footer>
                  <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
                    <ButtonPrime type="button" label="Cancel" severity="danger" raised size="small"
                      class="px-4 border-0" @click="addNGCDialog = false" />
                    <ButtonPrime type="button" severity="contrast" raised size="small" label="Add" class="px-4"
                      :disabled="addNGCLoading" @click="addNGC">
                      <template v-if="addNGCLoading" #icon>
                        <i class="fas fa-spinner fa-spin me-2"></i>
                      </template>
                    </ButtonPrime>
                  </div>
                </template>
              </DialogPrime>
            </div>
          </TabPanel>
          <!-- Material Type -->
          <TabPanel header="Material Type">
            <div v-if="detailLoading">
              <Skeleton height="2rem" class="mb-2" />
              <Skeleton width="80%" height="1.5rem" class="mb-2" />
              <Skeleton width="60%" height="1.5rem" class="mb-2" />
            </div>
            <div v-else-if="selectedDetail">
              <div class="mt-2 mb-2 d-flex justify-content-end">
                <ButtonPrime v-if="hasAnyPermission('item-name-create-material-type')" icon="pi pi-plus" label="Add"
                  size="small" style="background-color: black; color: white; border: none;"
                  @click="addMaterialTypeDialog = true" />
              </div>
              <DataTable v-if="selectedDetail.item_name_material_type && selectedDetail.item_name_material_type.length"
                :value="selectedDetail.item_name_material_type" class="datatable-sm text-sm" scrollable
                scroll-height="200px">
                <Column field="material_type" header="Material Type" header-class="bg-light text-dark" />
                <Column header="Actions" header-class="bg-light" :style="{ width: '150px' }">
                  <template #body="slotProps">
                    <ButtonPrime v-if="hasAnyPermission('item-name-delete-material-type')" v-tooltip.bottom="'Delete'"
                      icon="pi pi-trash" severity="danger" rounded size="small"
                      @click="deleteMaterialType(slotProps.data.id)" />
                  </template>
                </Column>
              </DataTable>
              <div v-else class="text-muted">Not Found data material type.</div>
              <DialogPrime v-model:visible="addMaterialTypeDialog" modal header="Add Material Type"
                style="width: 25rem;">
                <div class="mb-2">
                  <label for="material_type">Material Type<span class="text-danger">*</span></label>
                  <AutoComplete id="material_type" v-model="addMaterialTypeForm.material_type"
                    :suggestions="materialTypeOptions" :option-label="'label'" placeholder="Search Material Type..."
                    :dropdown="false" :force-selection="true" :invalid="addMaterialTypeError !== ''" fluid
                    @complete="searchMaterialType" @item-select="onMaterialTypeSelect" />
                </div>

                <template #footer>
                  <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
                    <ButtonPrime type="button" label="Cancel" severity="danger" raised size="small"
                      class="px-4 border-0" @click="addMaterialTypeDialog = false" />
                    <ButtonPrime type="button" severity="contrast" raised size="small" label="Add" class="px-4"
                      :disabled="addMaterialTypeLoading" @click="addMaterialType">
                      <span v-if="addMaterialTypeLoading"><i class="fas fa-spinner fa-spin"></i> Please wait...</span>
                      <span v-else>Add</span>
                    </ButtonPrime>
                  </div>
                </template>
              </DialogPrime>
            </div>
          </TabPanel>
          <!-- material group -->
          <TabPanel header="Material Group">
            <div v-if="detailLoading">
              <Skeleton height="2rem" class="mb-2" />
              <Skeleton width="80%" height="1.5rem" class="mb-2" />
              <Skeleton width="60%" height="1.5rem" class="mb-2" />
            </div>
            <div v-else-if="selectedDetail">
              <div class="mt-2 mb-2 d-flex justify-content-end">
                <ButtonPrime v-if="hasAnyPermission('item-name-create-material-group')" icon="pi pi-plus" label="Add"
                  size="small" style="background-color: black; color: white; border: none;"
                  @click="addMaterialGroupDialog = true" />
              </div>
              <DataTable
                v-if="selectedDetail.item_name_material_group && selectedDetail.item_name_material_group.length"
                :value="selectedDetail.item_name_material_group" class="datatable-sm text-sm" scrollable
                scroll-height="200px">
                <Column field="material_group" header="Material Group" header-class="bg-light text-dark" />
                <Column header="Actions" header-class="bg-light" :style="{ width: '150px' }">
                  <template #body="slotProps">
                    <ButtonPrime v-if="hasAnyPermission('item-name-delete-material-group')" v-tooltip.bottom="'Delete'"
                      icon="pi pi-trash" severity="danger" rounded size="small"
                      @click="deleteMaterialGroup(slotProps.data.id)" />
                  </template>
                </Column>
              </DataTable>
              <div v-else class="text-muted">Not Found.</div>
              <DialogPrime v-model:visible="addMaterialGroupDialog" modal header="Add Material Group"
                style="width: 25rem;">
                <div class="mb-2">
                  <label>Material Group<span class="text-danger">*</span></label>
                  <AutoComplete id="material_group" v-model="addMaterialGroupForm.material_group"
                    :suggestions="materialGroupOptions" :option-label="'label'" placeholder="Search Material Group..."
                    :dropdown="false" :force-selection="true" :invalid="addMaterialGroupError !== ''" fluid
                    @complete="searchMaterialGroup" @item-select="onMaterialGroupSelect" />
                </div>
                <template #footer>
                  <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
                    <ButtonPrime type="button" label="Cancel" severity="danger" raised size="small"
                      class="px-4 border-0" @click="addMaterialGroupDialog = false" />
                    <ButtonPrime type="button" severity="contrast" raised size="small" label="Add" class="px-4"
                      :disabled="addMaterialGroupLoading" @click="addMaterialGroup">
                      <template v-if="addMaterialGroupLoading" #icon>
                        <i class="fas fa-spinner fa-spin me-2"></i>
                      </template>
                    </ButtonPrime>
                  </div>
                </template>
              </DialogPrime>
            </div>
          </TabPanel>
          <!-- service category -->
          <TabPanel header="Service Category">
            <div v-if="detailLoading">
              <Skeleton height="2rem" class="mb-2" />
              <Skeleton width="80%" height="1.5rem" class="mb-2" />
              <Skeleton width="60%" height="1.5rem" class="mb-2" />
            </div>
            <div v-else-if="selectedDetail">
              <div class="mt-2 mb-2 d-flex justify-content-end">
                <ButtonPrime v-if="hasAnyPermission('item-name-create-service-category')" icon="pi pi-plus" label="Add"
                  size="small" style="background-color: black; color: white; border: none;"
                  @click="addServiceCategoryDialog = true" />
              </div>
              <DataTable
                v-if="selectedDetail.item_name_service_category && selectedDetail.item_name_service_category.length"
                :value="selectedDetail.item_name_service_category" class="datatable-sm text-sm" scrollable
                scroll-height="200px">
                <Column field="service_category" header="Service Category" header-class="bg-light text-dark" />
                <Column header="Description" header-class="bg-light text-dark">
                  <template #body="slotProps">
                    {{ slotProps.data.service_category_detail?.description || '-' }}
                  </template>
                </Column>
                <Column header="Actions" header-class="bg-light" :style="{ width: '150px' }">
                  <template #body="slotProps">
                    <ButtonPrime v-if="hasAnyPermission('item-name-delete-service-category')"
                      v-tooltip.bottom="'Delete'" icon="pi pi-trash" severity="danger" rounded size="small"
                      @click="deleteServiceCategory(slotProps.data.id)" />
                  </template>
                </Column>
              </DataTable>
              <div v-else class="text-muted">Not Found data service category.</div>
              <DialogPrime v-model:visible="addServiceCategoryDialog" modal header="Add Service Category"
                style="width: 25rem;">
                <div class="mb-2">
                  <label>Service Category<span class="text-danger">*</span></label>
                  <AutoComplete id="service_category" v-model="addServiceCategoryForm.service_category"
                    :suggestions="serviceCategoryOptions" :option-label="'label'"
                    placeholder="Search Service Category..." :dropdown="false" :force-selection="true"
                    :invalid="addServiceCategoryError !== ''" fluid @complete="searchServiceCategory"
                    @item-select="onServiceCategorySelect" />
                </div>
                <template #footer>
                  <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
                    <ButtonPrime type="button" label="Cancel" severity="danger" raised size="small"
                      class="px-4 border-0" @click="addServiceCategoryDialog = false" />
                    <ButtonPrime type="button" severity="contrast" raised size="small" label="Add" class="px-4"
                      :disabled="addServiceCategoryLoading" @click="addServiceCategory">
                      <template v-if="addServiceCategoryLoading" #icon>
                        <i class="fas fa-spinner fa-spin me-2"></i>
                      </template>
                    </ButtonPrime>
                  </div>
                </template>
              </DialogPrime>
            </div>
          </TabPanel>
          <!-- service group -->
          <TabPanel header="Service Group">
            <div v-if="detailLoading">
              <Skeleton height="2rem" class="mb-2" />
              <Skeleton width="80%" height="1.5rem" class="mb-2" />
              <Skeleton width="60%" height="1.5rem" class="mb-2" />
            </div>
            <div v-else-if="selectedDetail">
              <div class="mt-2 mb-2 d-flex justify-content-end">
                <ButtonPrime v-if="hasAnyPermission('item-name-create-service-group')" icon="pi pi-plus" label="Add"
                  size="small" style="background-color: black; color: white; border: none;"
                  @click="addServiceGroupDialog = true" />
              </div>
              <DataTable v-if="selectedDetail.item_name_service_group && selectedDetail.item_name_service_group.length"
                :value="selectedDetail.item_name_service_group" class="datatable-sm text-sm" scrollable
                scroll-height="200px">
                <Column field="service_group" header="Service Group" header-class="bg-light text-dark" />
                <Column header="Description" header-class="bg-light text-dark">
                  <template #body="slotProps">
                    {{ slotProps.data.service_group_detail?.description || '-' }}
                  </template>
                </Column>
                <Column header="Actions" header-class="bg-light" :style="{ width: '150px' }">
                  <template #body="slotProps">
                    <ButtonPrime v-if="hasAnyPermission('item-name-delete-service-group')" v-tooltip.bottom="'Delete'"
                      icon="pi pi-trash" severity="danger" rounded size="small"
                      @click="deleteServiceGroup(slotProps.data.id)" />
                  </template>
                </Column>
              </DataTable>
              <div v-else class="text-muted">Not Found data service group.</div>
              <DialogPrime v-model:visible="addServiceGroupDialog" modal header="Add Service Group"
                style="width: 25rem;">
                <div class="mb-2">
                  <label>Service Group<span class="text-danger">*</span></label>
                  <AutoComplete id="service_group" v-model="addServiceGroupForm.service_group"
                    :suggestions="serviceGroupOptions" :option-label="'label'" placeholder="Search Service Group..."
                    :dropdown="false" :force-selection="true" :invalid="addServiceGroupError !== ''" fluid
                    @complete="searchServiceGroup" @item-select="onServiceGroupSelect" />
                </div>
                <template #footer>
                  <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
                    <ButtonPrime type="button" label="Cancel" severity="danger" raised size="small"
                      class="px-4 border-0" @click="addServiceGroupDialog = false" />
                    <ButtonPrime type="button" severity="contrast" raised size="small" label="Add" class="px-4"
                      :disabled="addServiceGroupLoading" @click="addServiceGroup">
                      <template v-if="addServiceGroupLoading" #icon>
                        <i class="fas fa-spinner fa-spin me-2"></i>
                      </template>
                    </ButtonPrime>
                  </div>
                </template>
              </DialogPrime>
            </div>
          </TabPanel>
          <!-- images -->
          <TabPanel header="Image">
            <div v-if="detailLoading">
              <Skeleton height="2rem" class="mb-2" />
              <Skeleton width="80%" height="1.5rem" class="mb-2" />
              <Skeleton width="60%" height="1.5rem" class="mb-2" />
            </div>
            <div v-else-if="selectedDetail">
              <input ref="imageFileInput" type="file" accept="image/*" style="display: none;"
                @change="handleImageFileSelect" />
              <div class="d-flex flex-wrap gap-3 mt-3">
                <div v-for="img in selectedDetail.item_name_image" :key="img.id" class="position-relative"
                  style="width: 100px; height: 100px;">
                  <button v-if="hasAnyPermission('item-name-delete-image')"
                    class="btn btn-danger btn-sm rounded-circle position-absolute"
                    style="top: -6px; right: -6px; z-index: 2; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; padding: 0; box-shadow: 0 2px 4px rgba(0,0,0,0.15); font-size: 0.7rem;"
                    :disabled="deleteImageLoadingId === img.id" title="Delete image" @click="handleDeleteImage(img)">
                    <span v-if="deleteImageLoadingId === img.id"><i class="fas fa-spinner fa-spin"></i></span>
                    <span v-else><i class="fas fa-times"></i></span>
                  </button>
                  <PrimeImage v-if="imagePreviewUrls[img.id]" :src="imagePreviewUrls[img.id]" alt="item image" preview
                    :pt="{
                      image: { style: 'width: 100px; height: 100px; object-fit: cover; border-radius: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); cursor: pointer;' }
                    }" />
                  <div v-else class="d-flex align-items-center justify-content-center"
                    style="width: 100%; height: 100%; border-radius: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); border: 1px solid #dee2e6;">
                    <span style="font-size: 0.8rem;">Loading...</span>
                  </div>
                </div>
                <div v-if="hasAnyPermission('item-name-create-image')" class="position-relative"
                  style="width: 100px; height: 100px;">
                  <button :disabled="addImageLoading"
                    class="btn btn-outline-primary d-flex justify-content-center align-items-center"
                    style="width: 100%; height: 100%; font-size: 2rem; font-weight: 300; border-radius: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.08);"
                    @click="$refs.imageFileInput.click()">
                    <span v-if="addImageLoading"><i class="fas fa-spinner fa-spin"
                        style="font-size: 1.2rem;"></i></span>
                    <span v-else>+</span>
                  </button>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
    </div>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import NoAccess from "@/components/NoAccess.vue";
import BasePagination from "@/components/BasePagination.vue";
import moment from "moment";
import api from "@/helpers/api";
import standarItemNameService from "@/services/dictionary/standarItemNameService";
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import generalMasterDataService from "@/services/generalMasterData/generalMasterDataService";
import setMaxSize from "@/helpers/setMaxSize";

export default {
  components: {
    BasePagination,
    TabView,
    TabPanel,
    NoAccess,
  },
  data() {
    return {
      clientCode: '',
      companyCode: '',
      table: {
        onLoad: true,
        search: this.$route.query.search || "",
        total: 0,
        data: [],
        limit: 10,
        page: {
          current_pages: 1,
          per_pages: 10,
          total_pages: 1,
        },
        loadTimeout: null,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      modal: {
        visible: false,
        data: { inc: '', item_name: '', short_name: '', status: '', definition_ind: '', definition_eng: '' },
        errorMessage: {},
        onLoading: false,
        action: "",
        editId: null,
      },
      statusOptions: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
      ],
      selectedRowId: null,
      selectedDetail: null,
      detailLoading: false,
      activeDefinitionLang: 'eng',
      searchDialog: { visible: false },
      searchForm: { keyword: '', definition: '' },
      addColloquialDialog: false,
      addColloquialForm: { colloquial_name: '' },
      addNGCDialog: false,
      addNGCForm: { unspscObject: null },
      addNGCError: '',
      unspscOptions: [],
      addMaterialTypeDialog: false,
      addMaterialTypeForm: { material_type: null },
      materialTypeOptions: [],
      materialGroupOptions: [],
      addMaterialGroupDialog: false,
      addMaterialGroupForm: { material_group: null },
      addColloquialLoading: false,
      addMaterialTypeLoading: false,
      addMaterialGroupLoading: false,
      addNGCLoading: false,
      addMaterialTypeError: '',
      addMaterialGroupError: '',
      addColloquialError: '',
      addServiceCategoryDialog: false,
      addServiceCategoryForm: { service_category: null },
      serviceCategoryOptions: [],
      addServiceCategoryLoading: false,
      addServiceCategoryError: '',
      addServiceGroupDialog: false,
      addServiceGroupForm: { service_group: null },
      serviceGroupOptions: [],
      addServiceGroupLoading: false,
      addServiceGroupError: '',
      imagePreviewUrls: {},
      addImageLoading: false,
      deleteImageLoadingId: null,
      maxValidation: { size: 2000000, text: "Max Size 2000 KB" },
    };
  },
  watch: {
    selectedDetail(newVal) {
      if (newVal && newVal.item_name_image && newVal.item_name_image.length) {
        newVal.item_name_image.forEach(img => { this.fetchAndPreviewImage(img); });
      }
    }
  },
  mounted() {
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.clientCode = userAuth?.user?.client_code || '';
    this.companyCode = userAuth?.user?.company_code || '';
    if (this.$route.query.keyword || this.$route.query.definition) {
      this.table.search = {
        keyword: this.$route.query.keyword || '',
        definition: this.$route.query.definition || ''
      };
      this.searchForm.keyword = this.$route.query.keyword || '';
      this.searchForm.definition = this.$route.query.definition || '';
    }
    this.get();
    this.getMaxSizeConfig();
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    canSaveItemName() {
      return this.modal.editId ? this.hasAnyPermission('item-name-update') : this.hasAnyPermission('item-name-create');
    },
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    async searchUNSPSC(event) {
      const query = event.query;
      if (!query || query.length < 1) { this.unspscOptions = []; return; }
      try {
        const res = await api(this, standarItemNameService.listNGC({ search: query, limit: 10 }), false);
        this.unspscOptions = (res.data.data || []).map(u => ({
          label: `${u.identity_relation_code} - ${u.title}`,
          value: u
        }));
      } catch { this.unspscOptions = []; }
    },
    async addNGC() {
      this.addNGCError = '';
      const selected = this.addNGCForm.unspscObject;
      if (!selected) { this.addNGCError = 'Relation Code wajib diisi.'; return; }
      this.addNGCLoading = true;
      try {
        await api(this, standarItemNameService.createUnspsc({
          inc: this.selectedDetail.inc,
          identity_relation_code: selected.value.identity_relation_code
        }), true);
        this.addNGCDialog = false;
        this.addNGCForm.unspscObject = null;
        this.selectRow({ data: { id: this.selectedRowId } });
      } catch (e) { /* handled */ } finally { this.addNGCLoading = false; }
    },
    async deleteNGC(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, standarItemNameService.deleteUnspsc(id)).then(() => {
            this.selectRow({ data: { id: this.selectedRowId } });
          }).catch(() => { });
        }
      });
    },
    async selectRow(event) {
      const id = event.data.id;
      if (id !== this.selectedRowId) {
        Object.values(this.imagePreviewUrls).forEach(url => {
          if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
        });
        this.imagePreviewUrls = {};
      }
      this.selectedRowId = id;
      this.selectedDetail = null;
      this.detailLoading = true;
      try {
        const res = await api(this, standarItemNameService.detail(id), false);
        this.selectedDetail = res.data || null;
      } catch (e) { this.selectedDetail = null; } finally { this.detailLoading = false; }
    },
    rowClass(row) {
      return ['cursor-pointer', this.selectedRowId && row.id === this.selectedRowId ? 'bg-warning-custom' : ''];
    },
    async get() {
      this.table.onLoad = true;
      try {
        let params = { limit: this.table.limit, page: this.table.page.current_pages };
        if (this.table.search && typeof this.table.search === 'object') { Object.assign(params, this.table.search); }
        const response = await api(this, standarItemNameService.list(params), false);
        const paged = response.data || {};
        this.table.data = paged.data || [];
        this.table.total = paged.total || this.table.data.length;
        this.table.page.total_pages = paged.last_page || 1;
        this.table.page.current_pages = paged.current_page || 1;
        this.table.page.per_pages = paged.per_page || this.table.limit;
      } catch (error) { this.table.data = []; this.table.total = 0; } finally { this.table.onLoad = false; }
    },
    async searchMaterialGroup(event) {
      const query = event.query;
      if (!query || query.length < 1) { this.materialGroupOptions = []; return; }
      try {
        const res = await api(this, generalMasterDataService.list('material-group', { search: query, limit: 10, ...(this.companyCode !== 'MIND-ID' ? { client: this.clientCode } : {}) }), false);
        this.materialGroupOptions = (res.data.data || []).map(m => ({ label: `${m.code} - ${m.description}`, value: m.code }));
      } catch { this.materialGroupOptions = []; }
    },
    onMaterialGroupSelect(event) { this.addMaterialGroupForm.material_group = event.value; },
    async addMaterialGroup() {
      this.addMaterialGroupError = '';
      if (!this.addMaterialGroupForm.material_group) { this.addMaterialGroupError = 'Material Group wajib diisi'; return; }
      this.addMaterialGroupLoading = true;
      try {
        await api(this, standarItemNameService.createMaterialGroup({
          inc: this.selectedDetail.inc,
          material_group: this.addMaterialGroupForm.material_group.value,
        }), true);
        this.addMaterialGroupDialog = false;
        this.addMaterialGroupForm.material_group = null;
        this.selectRow({ data: { id: this.selectedRowId } });
      } catch (e) { /* handled */ } finally { this.addMaterialGroupLoading = false; }
    },
    async deleteMaterialGroup(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, standarItemNameService.deleteMaterialGroup(id)).then(() => {
            this.selectRow({ data: { id: this.selectedRowId } });
          }).catch(() => { });
        }
      });
    },
    async searchServiceCategory(event) {
      const query = event.query;
      if (!query || query.length < 1) { this.serviceCategoryOptions = []; return; }
      try {
        const res = await api(this, generalMasterDataService.list('service-category', { search: query, limit: 10, ...(this.companyCode !== 'MIND-ID' ? { client: this.clientCode } : {}) }), false);
        this.serviceCategoryOptions = (res.data.data || []).map(m => ({ label: `${m.code} - ${m.description}`, value: m.code }));
      } catch { this.serviceCategoryOptions = []; }
    },
    onServiceCategorySelect(event) { this.addServiceCategoryForm.service_category = event.value; },
    async addServiceCategory() {
      this.addServiceCategoryError = '';
      if (!this.addServiceCategoryForm.service_category) { this.addServiceCategoryError = 'Service Category wajib diisi'; return; }
      this.addServiceCategoryLoading = true;
      try {
        await api(this, standarItemNameService.createServiceCategory({
          inc: this.selectedDetail.inc,
          service_category: this.addServiceCategoryForm.service_category.value,
        }), true);
        this.addServiceCategoryDialog = false;
        this.addServiceCategoryForm.service_category = null;
        this.selectRow({ data: { id: this.selectedRowId } });
      } catch (e) { /* handled */ } finally { this.addServiceCategoryLoading = false; }
    },
    async deleteServiceCategory(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, standarItemNameService.deleteServiceCategory(id)).then(() => {
            this.selectRow({ data: { id: this.selectedRowId } });
          }).catch(() => { });
        }
      });
    },
    async searchServiceGroup(event) {
      const query = event.query;
      if (!query || query.length < 1) { this.serviceGroupOptions = []; return; }
      try {
        const res = await api(this, generalMasterDataService.list('service-group', { search: query, limit: 10, ...(this.companyCode !== 'MIND-ID' ? { client: this.clientCode } : {}) }), false);
        this.serviceGroupOptions = (res.data.data || []).map(m => ({ label: `${m.code} - ${m.description}`, value: m.code }));
      } catch { this.serviceGroupOptions = []; }
    },
    onServiceGroupSelect(event) { this.addServiceGroupForm.service_group = event.value; },
    async addServiceGroup() {
      this.addServiceGroupError = '';
      if (!this.addServiceGroupForm.service_group) { this.addServiceGroupError = 'Service Group wajib diisi'; return; }
      this.addServiceGroupLoading = true;
      try {
        await api(this, standarItemNameService.createServiceGroup({
          inc: this.selectedDetail.inc,
          service_group: this.addServiceGroupForm.service_group.value,
        }), true);
        this.addServiceGroupDialog = false;
        this.addServiceGroupForm.service_group = null;
        this.selectRow({ data: { id: this.selectedRowId } });
      } catch (e) { /* handled */ } finally { this.addServiceGroupLoading = false; }
    },
    async deleteServiceGroup(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, standarItemNameService.deleteServiceGroup(id)).then(() => {
            this.selectRow({ data: { id: this.selectedRowId } });
          }).catch(() => { });
        }
      });
    },
    async searchMaterialType(event) {
      const query = event.query;
      if (!query || query.length < 1) { this.materialTypeOptions = []; return; }
      try {
        const res = await api(this, generalMasterDataService.list('material-type', { search: query, limit: 10, ...(this.companyCode !== 'MIND-ID' ? { client: this.clientCode } : {}) }), false);
        this.materialTypeOptions = (res.data.data || []).map(m => ({ label: `${m.code} - ${m.description}`, value: m.code }));
      } catch { this.materialTypeOptions = []; }
    },
    onMaterialTypeSelect(event) { this.addMaterialTypeForm.material_type = event.value; },
    async addMaterialType() {
      this.addMaterialTypeError = '';
      if (!this.addMaterialTypeForm.material_type) { this.addMaterialTypeError = 'Material Type Required'; return; }
      this.addMaterialTypeLoading = true;
      try {
        await api(this, standarItemNameService.createMaterialType({
          inc: this.selectedDetail.inc,
          material_type: this.addMaterialTypeForm.material_type.value,
        }), true);
        this.addMaterialTypeDialog = false;
        this.addMaterialTypeForm.material_type = null;
        this.selectRow({ data: { id: this.selectedRowId } });
      } catch (e) { /* handled */ } finally { this.addMaterialTypeLoading = false; }
    },
    async deleteMaterialType(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, standarItemNameService.deleteMaterialType(id)).then(() => {
            this.selectRow({ data: { id: this.selectedRowId } });
          }).catch(() => { });
        }
      });
    },
    async addColloquial() {
      this.addColloquialError = '';
      if (!this.addColloquialForm.colloquial_name) { this.addColloquialError = 'Colloquial Name Required'; return; }
      this.addColloquialLoading = true;
      try {
        await api(this, standarItemNameService.createColloquial({
          inc: this.selectedDetail.inc,
          colloquial_name: this.addColloquialForm.colloquial_name,
        }), true);
        this.addColloquialDialog = false;
        this.addColloquialForm.colloquial_name = '';
        this.selectRow({ data: { id: this.selectedRowId } });
      } catch (e) { /* handled */ } finally { this.addColloquialLoading = false; }
    },
    async deleteColloquial(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, standarItemNameService.deleteColloquial(id)).then(() => {
            this.selectRow({ data: { id: this.selectedRowId } });
          }).catch(() => { });
        }
      });
    },
    openSearchDialog() {
      this.searchDialog.visible = true;
      this.searchForm = { keyword: this.table.search?.keyword || '', definition: this.table.search?.definition || '' };
    },
    closeSearchDialog() { this.searchDialog.visible = false; },
    doAdvancedSearch() {
      const searchObj = {};
      if (this.searchForm.keyword) searchObj.keyword = this.searchForm.keyword;
      if (this.searchForm.definition) searchObj.definition = this.searchForm.definition;
      this.table.search = searchObj;
      this.searchDialog.visible = false;
      this.table.page.current_pages = 1;
      this.$router.push({ query: { ...searchObj, page: 1 } });
      this.get();
    },
    filter() {
      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        this.$router.push({ path: this.$route.path, query: this.table.search ? { search: this.table.search } : null });
        this.table.page.current_pages = 1;
        this.get();
      }, 500);
    },
    changePage() { this.get(); },
    formatDate(date) { return date ? moment(date).format("DD MMMM YYYY, HH:mm") : "-"; },
    openAddModal() {
      this.modal.visible = true;
      this.modal.data = { inc: '', item_name: '', short_name: '', definition_eng: '', definition_ind: '', status: '' };
      this.modal.errorMessage = {};
      this.modal.editId = null;
      this.modal.action = "Add Item Name";
      this.modal.onLoading = false;
    },
    closeModal() { this.modal.visible = false; },
    openEditModal(rowData) {
      this.modal.visible = true;
      this.modal.action = "Edit Item Name";
      this.modal.editId = rowData.id;
      this.modal.data = { inc: rowData.inc || '', item_name: rowData.item_name || '', short_name: rowData.short_name || '', definition_eng: rowData.definition_eng || '', definition_ind: rowData.definition_ind || '', status: rowData.status || 'active' };
      this.modal.errorMessage = {};
      this.modal.onLoading = false;
    },
    async save() {
      this.modal.errorMessage = {};
      if (!this.modal.data.inc) this.modal.errorMessage['inc'] = 'Required INC';
      if (!this.modal.data.item_name) this.modal.errorMessage['item_name'] = 'Required Item Name';
      if (!this.modal.data.short_name) this.modal.errorMessage['short_name'] = 'Required Short Name';
      if (this.modal.editId && !this.modal.data.status) this.modal.errorMessage['status'] = 'Required Status';
      if (Object.keys(this.modal.errorMessage).length > 0) return;
      this.modal.onLoading = true;
      try {
        if (this.modal.editId) {
          await api(this, standarItemNameService.patch(this.modal.editId, { inc: this.modal.data.inc, item_name: this.modal.data.item_name, short_name: this.modal.data.short_name, definition_eng: this.modal.data.definition_eng || null, definition_ind: this.modal.data.definition_ind || null, status: this.modal.data.status || null }), true);
        } else {
          await api(this, standarItemNameService.create({ inc: this.modal.data.inc, item_name: this.modal.data.item_name, short_name: this.modal.data.short_name, definition_eng: this.modal.data.definition_eng || null, definition_ind: this.modal.data.definition_ind || null }), true);
        }
        this.modal.visible = false;
        this.modal.editId = null;
        this.get();
      } catch (error) {
        if (error.response?.data?.code == 400 && error.response.data.data) {
          error.response.data.data.forEach((v) => { this.modal.errorMessage[v.path] = v.msg; });
        }
      } finally { this.modal.onLoading = false; }
    },
    clearError(field) { if (this.modal.errorMessage[field]) { delete this.modal.errorMessage[field]; } },
    deleteItem(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, standarItemNameService.delete(id)).then(() => {
            if (this.selectedRowId === id) {
              this.selectedRowId = null;
              this.selectedDetail = null;
              this.selectedNGDetails = null;
              this.selectedNGCRow = null;
              // Revoke all preview URLs to clean memory
              Object.values(this.imagePreviewUrls).forEach(url => {
                if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
              });
              this.imagePreviewUrls = {};
            }
            this.get();
          }).catch(() => { });
        }
      });
    },
    async fetchAndPreviewImage(img) {
      if (!this.hasAnyPermission('item-name-view-image')) return;
      if (!img || !img.id || this.imagePreviewUrls[img.id]) return;
      try {
        const response = await standarItemNameService.getImageUrlById(img.id, { responseType: 'blob' });
        this.imagePreviewUrls[img.id] = URL.createObjectURL(response.data);
      } catch (e) { this.imagePreviewUrls[img.id] = null; }
    },
    async handleImageFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) { this.$toast.add({ severity: 'error', summary: 'Invalid File Type', detail: 'Only PNG and JPEG allowed', life: 3000 }); event.target.value = ''; return; }
      if (file.size > this.maxValidation.size) { this.$toast.add({ severity: 'error', summary: 'File Too Large', detail: `Exceeds ${this.maxValidation.text}`, life: 3000 }); event.target.value = ''; return; }
      this.addImageLoading = true;
      try {
        const formData = new FormData();
        formData.append('inc', this.selectedDetail.inc);
        formData.append('imageFile', file);
        await api(this, standarItemNameService.uploadImage(formData), true);
        event.target.value = '';
        this.selectRow({ data: { id: this.selectedRowId } });
      } catch (e) { /* handled */ } finally { this.addImageLoading = false; }
    },
    async handleDeleteImage(img) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          this.deleteImageLoadingId = img.id;
          api(this, standarItemNameService.deleteImage(img.id)).then(() => {
            this.selectRow({ data: { id: this.selectedRowId } });
          }).catch(() => { }).finally(() => { this.deleteImageLoadingId = null; });
        }
      });
    },
    toUpperCaseVal(event, field) {
      if (event && event.target && event.target.value) {
        const upperValue = event.target.value.toUpperCase();
        this.modal.data[field] = upperValue;
        event.target.value = upperValue;
      }
    },
    async getMaxSizeConfig() {
      try {
        const result = await setMaxSize();
        if (result) {
          this.maxValidation.size = result.data.data.maximum_file;
          this.maxValidation.text = `Max Size ${result.data.data.maximum_file / 1000} KB`;
        }
      } catch (error) {
        this.maxValidation.size = 2000000;
        this.maxValidation.text = `Max Size ${this.maxValidation.size / 1000} KB`;
      }
    },
  },
};
</script>

<style>
.bg-warning-custom {
  background-color: #FFE391 !important;
  color: rgb(22, 19, 19) !important;
}
</style>
